import React, { useState, useEffect, useRef } from "react";
import { ControlsDropdown } from "../base/ControlsDropdown";
import { PanelDropdown } from "../base/PanelDropdown";
import { useExtendedMutation } from "mutations";
import { getBooks, getChapters } from "api";
import "./BookCategoriesSettingsControls.css";

const BookCategoriesSettingsControls = ({ token, language, onSelectionChange, currentBookCategory, currentChapterSubcategory, onLoaded, onLoadingChange, variant = 'default' }) => {
  const Dropdown = variant === 'secondary' ? PanelDropdown : ControlsDropdown;
  const [books, setBooks] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [selectedBookCategory, setSelectedBookCategory] = useState('');
  const [selectedChapterSubcategory, setSelectedChapterSubcategory] = useState('');
  const [booksLoaded, setBooksLoaded] = useState(false);
  const [chaptersLoaded, setChaptersLoaded] = useState(false);
  const bookChangedInternally = useRef(false);

  const needsChapters = !!currentBookCategory;

  useEffect(() => {
    if (booksLoaded && (!needsChapters || chaptersLoaded)) onLoaded?.();
  }, [booksLoaded, chaptersLoaded]);

  const errorCallback = (error) => {
    console.log(error);
    onLoadingChange?.(false);
  };

  const getBooksMutation = useExtendedMutation(
    getBooks,
    (data) => { setBooks(data); setBooksLoaded(true); },
    errorCallback
  );

  const getChaptersMutation = useExtendedMutation(
    getChapters,
    (data) => { setChapters(data); setChaptersLoaded(true); onLoadingChange?.(false); },
    errorCallback
  );

  useEffect(() => {
    setBooks([]);
    setChapters([]);
    setSelectedBookCategory('');
    setSelectedChapterSubcategory('');
    onSelectionChange({ bookCategory: null, chapterSubcategory: null });
    getBooksMutation.mutateOnVirtualThread({ token, language });
  }, [language]);

  useEffect(() => {
    if (books.length === 1 && !selectedBookCategory) {
      const book = books[0];
      bookChangedInternally.current = true;
      setSelectedBookCategory(book.bookCode);
      onSelectionChange({ bookCategory: book.bookCode, chapterSubcategory: null });
      onLoadingChange?.(true);
      getChaptersMutation.mutateOnVirtualThread({ token, bookCode: book.bookCode });
    }
  }, [books]);

  useEffect(() => {
    if (chapters.length === 1 && !selectedChapterSubcategory) {
      const chapter = chapters[0];
      setSelectedChapterSubcategory(chapter.chapterNumber);
      onSelectionChange({ bookCategory: null, chapterSubcategory: chapter.chapterNumber });
    }
  }, [chapters]);

  useEffect(() => {
    if (currentBookCategory) {
      setSelectedBookCategory(currentBookCategory);
      if (!bookChangedInternally.current) {
        onLoadingChange?.(true);
        getChaptersMutation.mutateOnVirtualThread({ token, bookCode: currentBookCategory });
      }
      bookChangedInternally.current = false;
    }
    if (currentChapterSubcategory) setSelectedChapterSubcategory(currentChapterSubcategory);
  }, [currentBookCategory, currentChapterSubcategory]);

  const bookOptions = books.map(b => ({ value: b.bookCode, label: b.title }));
  const chapterOptions = chapters.map(c => ({ value: c.chapterNumber, label: c.title }));

  const handleBookChange = (e) => {
    const bookCategory = e.target.value;
    bookChangedInternally.current = true;
    setSelectedBookCategory(bookCategory);
    setSelectedChapterSubcategory('');
    setChapters([]);
    onSelectionChange({ bookCategory, chapterSubcategory: null });
    onLoadingChange?.(true);
    getChaptersMutation.mutateOnVirtualThread({ token, bookCode: bookCategory });
  };

  const handleChapterChange = (e) => {
    const chapterSubcategory = Number(e.target.value);
    setSelectedChapterSubcategory(chapterSubcategory);
    onSelectionChange({ bookCategory: null, chapterSubcategory });
  };

  const noBooks = bookOptions.length === 0;
  const onlyOneBook = bookOptions.length === 1;
  const noChapters = chapterOptions.length === 0;
  const onlyOneChapter = chapterOptions.length === 1;

  return (
    <div className="controls-inline-row">
      <div>
        <Dropdown
          label={"Choose Book"}
          options={bookOptions}
          onChange={handleBookChange}
          value={selectedBookCategory}
          disabled={noBooks || onlyOneBook}
          disabledTooltip={noBooks ? "No books available" : "Only one book available"}
        />
      </div>
      <div>
        <Dropdown
          label={"Choose Chapter"}
          options={chapterOptions}
          onChange={handleChapterChange}
          value={selectedChapterSubcategory}
          disabled={noChapters || onlyOneChapter}
          disabledTooltip={noChapters ? "No chapters available" : "Only one chapter available"}
        />
      </div>
    </div>
  );
};

export { BookCategoriesSettingsControls };
